import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { updateUserLanguage } from '../services/userService';
import { translations } from '../i18n';
import type { Language } from '../i18n';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: string, ...args: string[]) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'pl',
  setLanguage: async () => {},
  t: (key: string) => key,
});

function getNestedValue(obj: Record<string, any>, key: string): string | undefined {
  const parts = key.split('.');
  let current: any = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[part];
  }
  return typeof current === 'string' ? current : undefined;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { appUser } = useAuth();
  const [language, setLanguageState] = useState<Language>('pl');

  useEffect(() => {
    const lang = appUser?.language;
    if (lang === 'pl' || lang === 'en') {
      setLanguageState(lang);
    }
  }, [appUser?.language]);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = useCallback(async (lang: Language) => {
    setLanguageState(lang);
    if (appUser?.uid) {
      await updateUserLanguage(appUser.uid, lang);
    }
  }, [appUser?.uid]);

  const t = useCallback((key: string, ...args: string[]): string => {
    const dict = translations[language] as Record<string, any>;
    let result = getNestedValue(dict, key) ?? key;
    for (const arg of args) {
      result = result.replace('%s', arg);
    }
    return result;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}
